import _ from 'lodash';
import { type JSX } from 'react';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { QuestionEditView } from 'Components/UcrmTprm/QuestionnaireForm/QuestionEditView/QuestionEditView';
import { DueDiligenceQuestionType, QuestionnaireContent } from 'Models/DueDiligence';
import { UploadedFile } from 'Models/Files';
import { PublicControl } from 'Models/TPRM';

import styles from './ControlQuestionsView.module.css';

export interface ControlQuestionsViewProps {
    control: PublicControl;
    controlNumber: number;
    disabled: boolean;
    documentApi: DocumentApi;
    existingPendingFiles: File[];
    lastSavedQuestionnaire: QuestionnaireContent;
    selectedPendingFilesByQuestion: Map<string, File[]>;
    onControlChange: (updatedControl: PublicControl) => void;
    onSaveQuestionFileChanges: (questionId: string, newPendingFiles: File[], selectedPendingFiles: File[], selectedUploadedFiles: UploadedFile[]) => void;
    onUnableToAnswerChange: (questionId: string, isUnableToAnswer: boolean) => void;
}

/** For a questionnaire respondent (a third-party contact in TPRM, a SummIT user in UCRM) completing a questionnaire, renders all questions for a control. Note that the user doesn't see any information about the control itself. */
export const ControlQuestionsView = (props: ControlQuestionsViewProps): JSX.Element => {
    return (
        <div className={styles.questionsContainer}>
            {props.control.questions.map((question, index) => (
                <QuestionEditView
                    key={question.id}
                    question={question}
                    controlId={props.control.identifier}
                    documentApi={props.documentApi}
                    existingPendingFiles={props.existingPendingFiles}
                    lastSavedQuestionnaire={props.lastSavedQuestionnaire}
                    selectedPendingFiles={props.selectedPendingFilesByQuestion.get(question.id) ?? []}
                    onAnswerChange={(answer) => {
                        const updatedControl = _.cloneDeep(props.control);
                        const updatedQuestion = updatedControl.questions[index];
                        (updatedQuestion as any).answer = answer;

                        // Providing an answer means the third party is no longer unable to answer the question. Equivalent logic for File Upload questions exists in `ThirdPartyQuestionnaire`'s `handleSaveQuestionFileChanges`.
                        if (updatedQuestion._type === DueDiligenceQuestionType.DATE) {
                            updatedQuestion.is_unable_to_answer = false;
                        }
                        props.onControlChange(updatedControl);
                    }}
                    onCommentsChange={(comments) => {
                        const updatedControl = _.cloneDeep(props.control);
                        updatedControl.questions[index].comments = comments;
                        props.onControlChange(updatedControl);
                    }}
                    onSaveFileChanges={(newPendingFiles, selectedPendingFiles, selectedUploadedFiles) => {
                        props.onSaveQuestionFileChanges(question.id, newPendingFiles, selectedPendingFiles, selectedUploadedFiles);
                    }}
                    onUnableToAnswerChange={(isUnableToAnswer) => props.onUnableToAnswerChange(question.id, isUnableToAnswer)}
                    disabled={props.disabled}
                />
            ))}
        </div>
    );
};
